import { computed, ref } from 'vue';
import MessageApi from 'dashboard/api/inbox/message';

/**
 * Histórico de UMA conversa dentro do diálogo do board: paginação para trás,
 * busca e filtro de anexo sobre o que está carregado.
 *
 * Por que paginar e não carregar tudo: a conta tem conversas com centenas de
 * milhares de mensagens (a maior medida no acervo tem 284.131), e o servidor
 * nem oferece essa opção — `MessageFinder#messages_latest`
 * (app/finders/message_finder.rb:65) e `#messages_before` (:57) devolvem no
 * máximo `MESSAGES_PAGE_LIMIT` por chamada. Página com MENOS itens do que
 * esse limite — vazia ou não — é prova de fim de histórico: o finder só
 * devolve menos do que pediu quando não há mais linhas para dar, então esta
 * é a única forma de aprender isso sem gastar uma viagem extra que volta
 * vazia depois de uma primeira página já parcial.
 *
 * O CURSOR é o MENOR id carregado, não o primeiro da tela. O finder filtra por
 * `id < before` mas ORDENA por `created_at`, e nesta conta as duas ordens
 * divergem (mensagem importada pelo WAHA entra com id novo e created_at
 * antigo). Com o cursor do upstream (`messages[0].id`, o primeiro em ordem de
 * exibição) uma página já carregada pode voltar inteira e o histórico para de
 * avançar; com o menor id, tudo que volta é inédito por construção e o cursor
 * decresce em toda página — progresso garantido, sem deduplicação.
 */

// `file_type` chega no payload como o nome do enum de Attachment
// (`_message.json.jbuilder` -> `push_event_data` -> `base_data`), então o valor
// da opção É o valor comparado, sem tabela de tradução no meio. `all` e `any`
// não são tipos: são "sem filtro" e "tem anexo de qualquer tipo".
export const ATTACHMENT_FILTER_OPTIONS = [
  'all',
  'any',
  'image',
  'audio',
  'video',
  'file',
];

// O limite de página é do SERVIDOR (MessageFinder#messages_latest e
// #messages_before, app/finders/message_finder.rb:65 e :57 — os dois
// `limit(20)`). Replicá-lo aqui é o que permite reconhecer fim de histórico
// sem esperar uma viagem que volta vazia: uma página com menos itens do que
// o servidor devolveria se houvesse mais é, por construção, a última.
// Exportado para as specs montarem fixture de "página cheia" a partir da
// MESMA constante, em vez de duplicar o número 20 sem fonte.
export const MESSAGES_PAGE_LIMIT = 20;

// Busca insensível a acento e a caixa. O agente digita "nao" e "prazo" com a
// mesma frequência com que o cliente escreve "não" e "Prazo"; uma busca
// literal esconderia a mensagem e a tela não tem como avisar que escondeu.
const normalizeText = value =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

// O que conta como texto da mensagem para a busca. A transcrição do áudio
// entra porque áudio é o anexo mais frequente da conta (74.916) e, sem ela,
// buscar no que o cliente FALOU não acha nada. O assunto do e-mail entra pelo
// mesmo motivo em caixa de e-mail — são os mesmos campos que o
// `SearchService` do upstream indexa.
const searchableText = message =>
  [
    message.content,
    message.content_attributes?.email?.subject,
    ...(message.attachments ?? []).map(
      attachment => attachment.transcribed_text
    ),
  ]
    .filter(Boolean)
    .join(' ');

// Ordem da thread: `created_at` (segundos, como vem do jbuilder) e o id como
// desempate estável para mensagens do mesmo segundo.
const byThreadOrder = (a, b) =>
  (a.created_at ?? 0) - (b.created_at ?? 0) || a.id - b.id;

const hasAttachmentOfType = (message, fileType) =>
  (message.attachments ?? []).some(
    attachment => attachment.file_type === fileType
  );

/**
 * @param {Function} getConversationDisplayId devolve o display_id da conversa.
 * @returns {Object} estado e ações do histórico.
 */
export function useConversationThread(getConversationDisplayId) {
  const messages = ref([]);
  const isLoading = ref(true);
  const hasError = ref(false);
  const isLoadingOlder = ref(false);
  const hasOlderError = ref(false);
  const hasReachedStart = ref(false);

  const searchQuery = ref('');
  const attachmentFilter = ref('all');

  // Cache do texto normalizado por id: sem ele cada tecla digitada refaz o NFD
  // de todas as mensagens carregadas. Medido (node 22, mesma aritmética de
  // string do V8, 5.000 mensagens de ~18 palavras): 25,99 ms por tecla sem
  // cache contra 0,56 ms com cache, mais 24,92 ms de aquecimento pagos UMA vez
  // na chegada da página. 26 ms por tecla é digitação engasgando; 0,56 não é.
  const normalizedById = new Map();

  const rememberNormalized = list => {
    list.forEach(message => {
      normalizedById.set(message.id, normalizeText(searchableText(message)));
    });
  };

  const loadedCount = computed(() => messages.value.length);

  // O finder filtra por id; o cursor é o menor id carregado (ver cabeçalho).
  const oldestLoadedId = () =>
    messages.value.reduce(
      (oldest, message) => (message.id < oldest ? message.id : oldest),
      Infinity
    );

  const loadLatest = async () => {
    isLoading.value = true;
    hasError.value = false;
    hasOlderError.value = false;
    hasReachedStart.value = false;
    normalizedById.clear();
    try {
      const { data } = await MessageApi.getPreviousMessages({
        conversationId: getConversationDisplayId(),
      });
      const payload = data.payload ?? [];
      rememberNormalized(payload);
      messages.value = [...payload].sort(byThreadOrder);
      if (payload.length < MESSAGES_PAGE_LIMIT) hasReachedStart.value = true;
    } catch (error) {
      hasError.value = true;
    } finally {
      isLoading.value = false;
    }
  };

  const loadOlder = async () => {
    if (
      isLoading.value ||
      isLoadingOlder.value ||
      hasReachedStart.value ||
      messages.value.length === 0
    ) {
      return;
    }

    isLoadingOlder.value = true;
    hasOlderError.value = false;
    try {
      const { data } = await MessageApi.getPreviousMessages({
        conversationId: getConversationDisplayId(),
        before: oldestLoadedId(),
      });
      const payload = data.payload ?? [];
      if (payload.length > 0) {
        rememberNormalized(payload);
        messages.value = [...payload, ...messages.value].sort(byThreadOrder);
      }
      if (payload.length < MESSAGES_PAGE_LIMIT) hasReachedStart.value = true;
    } catch (error) {
      // Falha silenciosa aqui seria indistinguível de fim de histórico, e o
      // agente concluiria que a conversa começa onde a rede falhou.
      hasOlderError.value = true;
    } finally {
      isLoadingOlder.value = false;
    }
  };

  const isFiltering = computed(
    () => searchQuery.value.trim() !== '' || attachmentFilter.value !== 'all'
  );

  const visibleMessages = computed(() => {
    if (!isFiltering.value) return messages.value;

    const term = normalizeText(searchQuery.value.trim());
    const filter = attachmentFilter.value;

    return messages.value.filter(message => {
      if (filter === 'any' && (message.attachments ?? []).length === 0) {
        return false;
      }
      if (
        filter !== 'all' &&
        filter !== 'any' &&
        !hasAttachmentOfType(message, filter)
      ) {
        return false;
      }
      if (!term) return true;
      return (normalizedById.get(message.id) ?? '').includes(term);
    });
  });

  const visibleCount = computed(() => visibleMessages.value.length);

  const resetFilters = () => {
    searchQuery.value = '';
    attachmentFilter.value = 'all';
  };

  const isLoaded = messageId =>
    messages.value.some(message => String(message.id) === String(messageId));

  return {
    visibleMessages,
    loadedCount,
    visibleCount,
    isLoading,
    hasError,
    isLoadingOlder,
    hasOlderError,
    hasReachedStart,
    searchQuery,
    attachmentFilter,
    isFiltering,
    loadLatest,
    loadOlder,
    resetFilters,
    isLoaded,
  };
}
